import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { AssetBillingOutletContext } from '../components/layout/ProtectedAppShell';
import { useAuth } from '../contexts/AuthContext';
import { useUserContext } from '../hooks/useUserContext';
import { IconEnvelopeShield, IconEye, IconLaptop } from '../components/layout/NavIcons';
import { supabase } from '../lib/supabase';
import {
  checkThreats,
  generateBrief,
  getLockedAlertsCount,
  getMonthlyBill,
  getMonitoringStatus,
  getMyAssets,
  getSecurityScore,
  recalculateScore,
  type MonitoredAssetRow,
  type MonitoringStatus,
  type SecurityScoreData,
  type ScoreFactor,
} from '../server/api';
import { formatLastChecked, formatRelativeTime, startOfWeekMonday } from '../lib/timeFormat';

type AlertRow = {
  id: string;
  title: string;
  description: string;
  severity: string;
  created_at: string;
};

type ActivityItem = {
  key: string;
  tone: 'red' | 'amber' | 'green';
  text: string;
  timeLabel: string;
};

function greetingForHour(h: number): string {
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function scoreVisual(score: number, isPersonal: boolean): { color: string; label: string } {
  if (score >= 70) {
    return {
      color: 'var(--accent-green)',
      label: isPersonal ? 'You are well protected' : 'Your business is well protected',
    };
  }
  if (score >= 40) return { color: 'var(--accent-amber)', label: 'Some risks need your attention' };
  return {
    color: 'var(--accent-danger)',
    label: isPersonal ? 'You are at risk — act now' : 'Your business is at risk — act now',
  };
}

function severityTone(sev: string): 'red' | 'amber' | 'green' {
  const s = sev.toLowerCase();
  if (s.includes('high') || s.includes('critical') || s.includes('severe')) return 'red';
  if (s.includes('medium') || s.includes('warn')) return 'amber';
  return 'green';
}

function scoreTone(score: number): 'red' | 'amber' | 'green' {
  if (score >= 70) return 'green';
  if (score >= 40) return 'amber';
  return 'red';
}

type OtxThreatLevel = 'low' | 'medium' | 'high';

function threatRank(s: OtxThreatLevel): number {
  if (s === 'high') return 3;
  if (s === 'medium') return 2;
  return 1;
}

function worstOtxStatus(results: Array<{ status?: string }>): OtxThreatLevel {
  let worst: OtxThreatLevel = 'low';
  for (const r of results) {
    const st = r?.status;
    if (st === 'high' || st === 'medium' || st === 'low') {
      if (threatRank(st) > threatRank(worst)) worst = st;
    }
  }
  return worst;
}

const R = 88;
const C = 2 * Math.PI * R;

function SecurityScoreGauge({ score, label, color }: { score: number; label: string; color: string }) {
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const to = score;
    const duration = 1250;
    let frameId = 0;

    function tick(now: number) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setAnimatedScore(Math.round(from + (to - from) * eased));
      if (t < 1) frameId = window.requestAnimationFrame(tick);
    }

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [score]);

  const offset = C * (1 - animatedScore / 100);

  return (
    <div className="flex flex-col items-center">
      <div className="relative mx-auto h-[220px] w-[220px] sm:h-[260px] sm:w-[260px]">
        <svg
          className="-rotate-90 transform"
          viewBox="0 0 200 200"
          aria-hidden
          width="100%"
          height="100%"
        >
          <circle cx="100" cy="100" r={R} fill="none" stroke="var(--bg-card)" strokeWidth="12" />
          <circle
            cx="100"
            cy="100"
            r={R}
            fill="none"
            stroke={color}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={C}
            style={{ strokeDashoffset: offset }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-5xl font-bold tabular-nums tracking-tight text-foreground sm:text-6xl">
            {Math.round(animatedScore)}
          </span>
          <span className="mt-1 text-xs font-medium uppercase tracking-wider text-muted">Security Health</span>
        </div>
      </div>
      <p className="mt-2 max-w-sm text-center text-sm font-medium text-foreground sm:text-base">{label}</p>
    </div>
  );
}

function staggerStyle(i: number): React.CSSProperties {
  return { animationDelay: `${120 + i * 85}ms` };
}

export default function DashboardPage() {
  const { expiredAssetRows } = useOutletContext<AssetBillingOutletContext>();
  const { currentUser, userProfile } = useAuth();
  const { isPersonal, display, features } = useUserContext();
  const userId = currentUser?.id;

  const [securityScore, setSecurityScore] = useState(0);
  const [securityScoreDataReady, setSecurityScoreDataReady] = useState(false);
  const [scoreData, setScoreData] = useState<SecurityScoreData | null>(null);
  const [scoreRefreshLoading, setScoreRefreshLoading] = useState(false);
  const [scoreCooldownUntil, setScoreCooldownUntil] = useState<number>(0);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date>(new Date());
  const [emailBlocked, setEmailBlocked] = useState<number | null>(null);
  const [darkWebCount, setDarkWebCount] = useState<number>(0);
  const [recentAlerts, setRecentAlerts] = useState<AlertRow[]>([]);
  const [dataReady, setDataReady] = useState(false);
  const [totalAlertCount, setTotalAlertCount] = useState(0);
  const [monitoredDomainValues, setMonitoredDomainValues] = useState<string[]>([]);
  const [threatCheckLoading, setThreatCheckLoading] = useState(false);
  const [otxThreatStatus, setOtxThreatStatus] = useState<OtxThreatLevel | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefText, setBriefText] = useState<string | null>(null);
  const [briefError, setBriefError] = useState(false);
  const [briefGeneratedAt, setBriefGeneratedAt] = useState<Date | null>(null);
  const [billingSummary, setBillingSummary] = useState<{
    assetCount: number;
    trialAssetCount: number;
    earliestTrialExpiresAt: string | null;
    estimatedMonthlyCost: number;
    currency: string;
  } | null>(null);
  const [monitoredAssets, setMonitoredAssets] = useState<MonitoredAssetRow[]>([]);
  const [monitoringStatus, setMonitoringStatus] = useState<MonitoringStatus | null>(null);
  const [lockedThreatCount, setLockedThreatCount] = useState(0);

  const hour = new Date().getHours();
  const greeting = greetingForHour(hour);
  const displayName = useMemo(() => {
    if (!userProfile) return 'there';
    if (isPersonal) {
      return userProfile.owner_name?.trim() || 'there';
    }
    return userProfile.business_name?.trim() || 'Your business';
  }, [userProfile, isPersonal]);
  const country = userProfile?.country?.trim() || '—';
  const complianceLabel = userProfile?.compliance_framework?.trim() || '—';

  const greetingSubtitle = useMemo(() => {
    if (!userProfile) return '';
    if (isPersonal) {
      return `${country} · Protected under standard monitoring`;
    }
    return `${country} · ${complianceLabel} · Protected under standard monitoring`;
  }, [userProfile, country, complianceLabel, isPersonal]);

  const visual = useMemo(() => scoreVisual(securityScore, Boolean(isPersonal)), [securityScore, isPersonal]);

  const refreshBrief = useCallback(async () => {
    if (!userProfile) return;
    setBriefLoading(true);
    setBriefError(false);
    setBriefText(null);
    setBriefGeneratedAt(null);
    try {
      const data = await generateBrief({
        businessName: displayName,
        country: userProfile.country?.trim() || '',
        industry: userProfile.industry?.trim() || 'General',
        alertCount: totalAlertCount,
        score: securityScore,
        accountType: isPersonal ? 'personal' : 'business',
      });
      if (data?.brief && typeof data.brief === 'string' && data.brief.trim()) {
        setBriefText(data.brief.trim());
        setBriefGeneratedAt(new Date());
      } else {
        setBriefError(true);
      }
    } catch {
      setBriefError(true);
    } finally {
      setBriefLoading(false);
    }
  }, [userProfile, totalAlertCount, securityScore, displayName, isPersonal]);

  useEffect(() => {
    if (!userId || !userProfile) return;
    const profile = userProfile;

    let cancelled = false;
    let briefTimer: number | undefined;

    async function load() {
      setDataReady(false);
      setSecurityScoreDataReady(false);
      setScoreData(null);
      setBriefLoading(false);
      setBriefError(false);
      setBriefText(null);
      setBriefGeneratedAt(null);
      setThreatCheckLoading(true);
      setOtxThreatStatus(null);
      setMonitoringStatus(null);
      setLockedThreatCount(0);
      const weekStart = startOfWeekMonday().toISOString();

      const [scoreRes, monRes, lockRes] = await Promise.all([
        getSecurityScore(),
        getMonitoringStatus(),
        getLockedAlertsCount(),
      ]);

      if (cancelled) return;

      const [emailRes, darkRes, recentRes, alertsCountRes, myAssetsRes, monthlyBillRes] = await Promise.all([
        supabase
          .from('alerts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('category', 'email')
          .gte('created_at', weekStart),
        supabase
          .from('alerts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('category', 'dark_web'),
        supabase
          .from('alerts')
          .select('id, title, description, severity, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(5),
        supabase.from('alerts').select('*', { count: 'exact', head: true }).eq('user_id', userId),
        getMyAssets(),
        getMonthlyBill(),
      ]);

      if (cancelled) return;

      if (monRes.ok) setMonitoringStatus(monRes.status);
      else setMonitoringStatus(null);
      setLockedThreatCount(lockRes.ok ? lockRes.count : 0);

      let scoreVal = 0;
      if (scoreRes.ok) {
        scoreVal = scoreRes.score.overall;
        setScoreData(scoreRes.score);
        setSecurityScore(scoreRes.score.overall);
        setLastCheckedAt(new Date(scoreRes.score.calculated_at));
      } else {
        setScoreData(null);
        setSecurityScore(0);
        setLastCheckedAt(new Date());
      }
      if (!cancelled) setSecurityScoreDataReady(true);

      if (emailRes.error || emailRes.count === null) setEmailBlocked(0);
      else setEmailBlocked(emailRes.count);

      if (darkRes.error || darkRes.count === null) setDarkWebCount(0);
      else setDarkWebCount(darkRes.count);

      if (!recentRes.error && recentRes.data) setRecentAlerts(recentRes.data as AlertRow[]);
      else setRecentAlerts([]);

      const alertTotal = alertsCountRes.error || alertsCountRes.count === null ? 0 : alertsCountRes.count;
      setTotalAlertCount(alertTotal);

      const assetsData: MonitoredAssetRow[] = myAssetsRes.ok ? myAssetsRes.monitored : [];
      setMonitoredAssets(assetsData);

      const domainRows = assetsData.filter(
        (row) => row.asset_type === 'domain' && row.billing_status !== 'cancelled'
      );
      const domainValues = domainRows.map((r) => String(r.asset_value).trim()).filter(Boolean);
      setMonitoredDomainValues(domainValues);

      const activeAssets = assetsData.filter((row) => row.billing_status !== 'cancelled');
      const trialRows = activeAssets.filter((row) => row.is_trial === true);
      const earliestTrial = trialRows
        .map((row) => (typeof row.trial_expires_at === 'string' ? row.trial_expires_at : null))
        .filter((v): v is string => Boolean(v))
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;
      const monthlyCost = monthlyBillRes.ok ? monthlyBillRes.bill.total : 0;
      const currency = monthlyBillRes.ok ? monthlyBillRes.bill.currency : 'USD';
      setBillingSummary({
        assetCount: activeAssets.length,
        trialAssetCount: trialRows.length,
        earliestTrialExpiresAt: earliestTrial,
        estimatedMonthlyCost: monthlyCost,
        currency,
      });

      if (domainValues.length > 0) {
        try {
          const threatResults = await Promise.all(domainValues.map((d) => checkThreats(d)));
          if (!cancelled) setOtxThreatStatus(worstOtxStatus(threatResults));
        } catch {
          if (!cancelled) setOtxThreatStatus('low');
        }
      } else if (!cancelled) {
        setOtxThreatStatus(null);
      }
      if (!cancelled) setThreatCheckLoading(false);

      briefTimer = window.setTimeout(() => {
        void (async () => {
          if (cancelled) return;
          setBriefLoading(true);
          setBriefError(false);
          try {
            const briefDisplayName = isPersonal
              ? profile.owner_name?.trim() || 'there'
              : profile.business_name?.trim() || 'Your business';
            const briefData = await generateBrief({
              businessName: briefDisplayName,
              country: profile.country?.trim() || '',
              industry: profile.industry?.trim() || 'General',
              alertCount: alertTotal,
              score: scoreVal,
              accountType: isPersonal ? 'personal' : 'business',
            });
            if (!cancelled) {
              if (briefData?.brief && typeof briefData.brief === 'string' && briefData.brief.trim()) {
                setBriefText(briefData.brief.trim());
                setBriefGeneratedAt(new Date());
                setBriefError(false);
              } else {
                setBriefError(true);
              }
            }
          } catch {
            if (!cancelled) setBriefError(true);
          } finally {
            if (!cancelled) setBriefLoading(false);
          }
        })();
      }, 2000);

      if (!cancelled) setDataReady(true);
    }

    void load();
    return () => {
      cancelled = true;
      if (briefTimer !== undefined) window.clearTimeout(briefTimer);
    };
  }, [userId, userProfile, isPersonal]);

  const emailMetric = !dataReady || emailBlocked === null ? null : emailBlocked;
  const scoreGaugeReady = dataReady && securityScoreDataReady;
  const darkWeb = darkWebCount;
  const hasMonitoredDomains = monitoredDomainValues.length > 0;

  const darkWebBadge = useMemo(() => {
    if (hasMonitoredDomains) {
      if (threatCheckLoading) {
        return {
          label: 'Checking…',
          className: 'bg-foreground/10 text-muted animate-pulse',
        };
      }
      const st = otxThreatStatus ?? 'low';
      if (st === 'high') {
        return { label: 'Action Needed', className: 'bg-danger/15 text-danger' };
      }
      if (st === 'medium') {
        return { label: 'Monitor Closely', className: 'bg-warning/15 text-warning' };
      }
      return { label: 'All Clear', className: 'bg-accent/15 text-accent' };
    }
    if (darkWeb === 0) {
      return { label: 'All Clear', className: 'bg-accent/15 text-accent' };
    }
    return { label: 'Action Needed', className: 'bg-danger/15 text-danger' };
  }, [hasMonitoredDomains, threatCheckLoading, otxThreatStatus, darkWeb]);

  const darkWebDescription = useMemo(() => {
    if (hasMonitoredDomains) {
      if (threatCheckLoading) {
        return 'Scanning global threat intelligence for your domains...';
      }
      const st = otxThreatStatus ?? 'low';
      if (st === 'high') {
        return 'Active threats detected for your domain — immediate action recommended';
      }
      if (st === 'medium') {
        return 'Potential security signals detected — we recommend reviewing your alerts';
      }
      return 'No threats detected across global security databases for your monitored domains';
    }
    if (darkWeb === 0) {
      return display.breachMessage;
    }
    return 'Your data was found in security databases — check your alerts';
  }, [hasMonitoredDomains, threatCheckLoading, otxThreatStatus, darkWeb, display.breachMessage]);

  const darkWebCardAccentStyle = useMemo((): React.CSSProperties => {
    if (!hasMonitoredDomains || threatCheckLoading) return {};
    if (otxThreatStatus === 'high') return { borderLeft: '3px solid var(--accent-danger)' };
    if (otxThreatStatus === 'medium') return { borderLeft: '3px solid #F59E0B' };
    return {};
  }, [hasMonitoredDomains, threatCheckLoading, otxThreatStatus]);

  const activityItems: ActivityItem[] = useMemo(() => {
    if (recentAlerts.length > 0) {
      return recentAlerts.map((a) => ({
        key: a.id,
        tone: severityTone(a.severity),
        text: a.title || a.description,
        timeLabel: (() => {
          try {
            return formatRelativeTime(a.created_at);
          } catch {
            return '';
          }
        })(),
      }));
    }
    if (monitoringStatus?.hasSoftStoppedAssets && lockedThreatCount > 0) {
      const n = Math.min(lockedThreatCount, 5);
      return Array.from({ length: n }, (_, i) => ({
        key: `locked-activity-${i}`,
        tone: 'amber' as const,
        text: '🔒 Security threat detected — upgrade to view details',
        timeLabel: '',
      }));
    }
    return [];
  }, [recentAlerts, monitoringStatus?.hasSoftStoppedAssets, lockedThreatCount]);

  const lastCheckedLabel = useMemo(() => {
    try {
      return formatLastChecked(lastCheckedAt);
    } catch {
      return '—';
    }
  }, [lastCheckedAt]);

  const scoreLastCalculatedLabel = useMemo(() => {
    if (!scoreData?.calculated_at) return null;
    try {
      return formatRelativeTime(scoreData.calculated_at);
    } catch {
      return null;
    }
  }, [scoreData?.calculated_at]);

  const scoreTrend = useMemo(() => {
    const delta = scoreData?.score_change;
    if (typeof delta !== 'number' || delta === 0) return null;
    const sign = delta > 0 ? '+' : '';
    return {
      label: `${delta > 0 ? '▲' : '▼'} ${sign}${delta} from last check`,
      tone: delta > 0 ? 'green' : 'red',
    } as const;
  }, [scoreData?.score_change]);

  const deviceRisk = useMemo(() => {
    if (!scoreData) {
      return {
        label: '—',
        badge: 'Checking...',
        badgeStyle: { backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)' },
        labelStyle: { color: 'var(--text-secondary)' },
        description: 'Checking device security status...',
      };
    }

    const deviceActive = scoreData.dimensions_active?.includes('Device Security') === true;
    if (!deviceActive || scoreData.device_score === null) {
      return {
        label: 'None enrolled',
        badge: 'Not monitored',
        badgeStyle: { backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)' },
        labelStyle: { color: 'var(--text-secondary)' },
        description:
          'No devices enrolled. Download the ShieldSME agent to start monitoring your devices.',
      };
    }

    const s = scoreData.device_score;
    if (s >= 80) {
      return {
        label: 'Low',
        badge: 'Low Risk',
        badgeStyle: {
          backgroundColor: 'color-mix(in srgb, var(--accent-green) 15%, transparent)',
          color: 'var(--accent-green)',
        },
        labelStyle: { color: 'var(--accent-green)' },
        description: 'Your connected devices appear to be in good shape.',
      };
    }
    if (s >= 50) {
      return {
        label: 'Medium',
        badge: 'Medium Risk',
        badgeStyle: {
          backgroundColor: 'color-mix(in srgb, #F59E0B 15%, transparent)',
          color: '#B45309',
        },
        labelStyle: { color: '#F59E0B' },
        description: 'Some device issues need your attention. Check your alerts for details.',
      };
    }
    return {
      label: 'High',
      badge: 'High Risk',
      badgeStyle: {
        backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)',
        color: 'var(--accent-danger)',
      },
      labelStyle: { color: 'var(--accent-danger)' },
      description: 'Your devices have security issues that need immediate attention.',
    };
  }, [scoreData]);

  const dimensionRows = useMemo(() => {
    const s = scoreData;
    if (!s) return [];
    const rows: Array<{ key: string; label: string; score: number }> = [];
    if (typeof s.email_score === 'number') rows.push({ key: 'email', label: 'Email Security', score: s.email_score });
    if (typeof s.domain_score === 'number') rows.push({ key: 'domain', label: 'Domain Security', score: s.domain_score });
    if (typeof s.device_score === 'number') rows.push({ key: 'device', label: 'Device Security', score: s.device_score });
    if (typeof s.dark_web_score === 'number') rows.push({ key: 'dark_web', label: 'Dark Web Exposure', score: s.dark_web_score });
    if (typeof s.network_score === 'number') rows.push({ key: 'network', label: 'Network Security', score: s.network_score });
    if (typeof s.momo_score === 'number') rows.push({ key: 'momo', label: 'MoMo Protection', score: s.momo_score });
    return rows;
  }, [scoreData]);

  const factorRows: ScoreFactor[] = useMemo(() => {
    const f = scoreData?.score_factors;
    if (!Array.isArray(f)) return [];
    return f.slice(0, 5);
  }, [scoreData?.score_factors]);

  const refreshScore = useCallback(async () => {
    if (scoreRefreshLoading) return;
    if (scoreCooldownUntil && Date.now() < scoreCooldownUntil) return;
    setScoreRefreshLoading(true);
    try {
      const res = await recalculateScore();
      if (res.ok) {
        setScoreData(res.score);
        setSecurityScore(res.score.overall);
        setLastCheckedAt(new Date(res.score.calculated_at));
        setScoreCooldownUntil(Date.now() + 5 * 60 * 1000);
      }
    } finally {
      setScoreRefreshLoading(false);
    }
  }, [scoreRefreshLoading, scoreCooldownUntil]);
  const earliestTrialDaysLeft = useMemo(() => {
    if (!billingSummary?.earliestTrialExpiresAt) return null;
    const ms = new Date(billingSummary.earliestTrialExpiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [billingSummary?.earliestTrialExpiresAt]);

  const hasActiveMonitoring =
    monitoredAssets.length > 0 &&
    monitoredAssets.some(
      (a) => a.billing_status !== 'expired' && a.billing_status !== 'cancelled'
    );
  const hasExpiredTrialAsset = monitoredAssets.some((a) => a.billing_status === 'expired');

  return (
    <div className="mx-auto max-w-6xl space-y-10">
      <section
        className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
        style={staggerStyle(0)}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {greeting}, {displayName}
            </h2>
            <p className="mt-2 text-sm text-muted sm:text-base">{greetingSubtitle}</p>
          </div>
          {hasActiveMonitoring ? (
            <div className="flex items-center gap-2 self-start rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:text-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-40" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              Monitoring active
            </div>
          ) : null}
          {!hasActiveMonitoring && monitoredAssets.length === 0 ? (
            <div className="flex items-center gap-2 self-start rounded-full border border-border bg-foreground/5 px-3 py-1.5 text-xs font-medium text-muted sm:text-sm">
              <span className="relative flex h-2 w-2">
                <span className="relative inline-flex h-2 w-2 rounded-full bg-muted" />
              </span>
              No assets monitored
            </div>
          ) : null}
          {!hasActiveMonitoring && monitoredAssets.length > 0 && hasExpiredTrialAsset ? (
            <div
              className="flex items-center gap-2 self-start rounded-full border px-3 py-1.5 text-xs font-medium sm:text-sm"
              style={{ borderColor: '#F59E0B55', backgroundColor: '#F59E0B14', color: '#F59E0B' }}
            >
              <span className="relative flex h-2 w-2">
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
              </span>
              Action required
            </div>
          ) : null}
          {!hasActiveMonitoring && monitoredAssets.length > 0 && !hasExpiredTrialAsset ? (
            <div className="flex items-center gap-2 self-start rounded-full border border-border bg-foreground/5 px-3 py-1.5 text-xs font-medium text-muted sm:text-sm">
              <span className="relative flex h-2 w-2">
                <span className="relative inline-flex h-2 w-2 rounded-full bg-muted" />
              </span>
              No active monitoring
            </div>
          ) : null}
        </div>
      </section>

      {expiredAssetRows.length > 0 ? (
        <section
          className="opacity-0 animate-fade-in-up rounded-2xl border border-danger/50 bg-danger/10 p-5 sm:p-6"
          style={staggerStyle(1)}
          aria-label="Expired asset trials"
        >
          <h3 className="text-lg font-semibold text-foreground">Action Required — Trial Expired</h3>
          <p className="mt-2 text-sm text-muted">
            The following monitored assets are no longer on trial. Upgrade to keep full protection.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-foreground">
            {expiredAssetRows.map((a) => (
              <li key={a.id} className="rounded-lg border border-border/60 bg-surface/40 px-3 py-2">
                <span className="font-medium capitalize">{a.asset_type.replace(/_/g, ' ')}</span>
                <span className="text-muted"> · </span>
                <span>{a.asset_value}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/marketplace"
            className="mt-4 inline-flex text-sm font-semibold text-accent underline-offset-2 hover:underline"
          >
            View pricing →
          </Link>
        </section>
      ) : null}

      <section
        className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/80 p-6 sm:p-10"
        style={staggerStyle(2)}
      >
        {!scoreGaugeReady ? (
          <div
            className="mx-auto flex h-[220px] w-[220px] flex-col items-center justify-center gap-4 sm:h-[260px] sm:w-[260px]"
            aria-busy="true"
            aria-label="Loading security score"
          >
            <div className="h-40 w-40 animate-pulse rounded-full border-8 border-foreground/10 border-t-accent/40 sm:h-48 sm:w-48" />
            <p className="text-center text-sm text-muted">Loading security score…</p>
          </div>
        ) : (
          <SecurityScoreGauge score={securityScore} label={visual.label} color={visual.color} />
        )}
        <div className="mt-4 flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-muted">Last checked: {lastCheckedLabel}</p>
          {scoreLastCalculatedLabel ? (
            <p className="text-xs text-muted">Last calculated: {scoreLastCalculatedLabel}</p>
          ) : null}
          {scoreTrend ? (
            <p className={`text-xs font-semibold ${scoreTrend.tone === 'green' ? 'text-accent' : 'text-danger'}`}>
              {scoreTrend.label}
            </p>
          ) : null}
          <button
            type="button"
            onClick={refreshScore}
            disabled={scoreRefreshLoading || (scoreCooldownUntil ? Date.now() < scoreCooldownUntil : false)}
            className="mt-1 inline-flex items-center justify-center rounded-lg border border-border bg-surface/60 px-3 py-2 text-xs font-semibold text-foreground hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
          >
            {scoreRefreshLoading ? 'Refreshing…' : 'Refresh score'}
          </button>
          {monitoringStatus?.hasSoftStoppedAssets ? (
            <p className="max-w-md text-xs text-warning">
              Score may be lower due to undetected threats while monitoring is paused.{' '}
              <Link to="/marketplace" className="font-semibold text-accent underline-offset-2 hover:underline">
                Upgrade to restore coverage →
              </Link>
            </p>
          ) : null}
        </div>

        {dimensionRows.length > 0 ? (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-foreground">Score breakdown</h3>
            <div className="mt-3 space-y-3">
              {dimensionRows.map((d) => {
                const tone = scoreTone(d.score);
                const barClass =
                  tone === 'green' ? 'bg-accent' : tone === 'amber' ? 'bg-warning' : 'bg-danger';
                return (
                  <div key={d.key} className="grid grid-cols-[1fr,140px,40px] items-center gap-3">
                    <p className="text-xs font-medium text-muted sm:text-sm">{d.label}</p>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/10">
                      <div className={`h-full ${barClass}`} style={{ width: `${Math.max(0, Math.min(100, d.score))}%` }} />
                    </div>
                    <p className="text-right text-xs font-semibold tabular-nums text-foreground">{Math.round(d.score)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {factorRows.length > 0 ? (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-foreground">What&apos;s affecting your score</h3>
            <ul className="mt-3 space-y-2">
              {factorRows.map((f, idx) => {
                const tone = f.severity === 'positive' ? 'green' : f.severity === 'warning' ? 'amber' : 'red';
                const dotClass = tone === 'green' ? 'bg-accent' : tone === 'amber' ? 'bg-warning' : 'bg-danger';
                const prefix = tone === 'green' ? '▲' : tone === 'amber' ? '⚠' : '✕';
                return (
                  <li key={`${idx}-${f.label}`} className="flex items-start gap-3 rounded-lg border border-border/60 bg-surface/50 px-3 py-2">
                    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${dotClass}`} aria-hidden />
                    <p className="min-w-0 flex-1 text-xs text-foreground sm:text-sm">
                      <span className="font-semibold">{prefix}</span> {f.label}
                    </p>
                    <span className="text-xs font-semibold tabular-nums text-muted">
                      {f.impact > 0 ? `+${Math.round(f.impact)}` : Math.round(f.impact)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
          style={staggerStyle(3)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="rounded-xl bg-accent/10 p-3 text-accent">
              <IconEnvelopeShield className="h-7 w-7" />
            </div>
            <span className="rounded-full bg-foreground/5 px-2.5 py-1 text-xs font-medium text-muted">
              This Week
            </span>
          </div>
          <p className="mt-4 text-sm font-medium text-muted">Email Threats Blocked</p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {emailMetric === null ? '—' : emailMetric}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            Suspicious emails blocked before reaching your inbox
          </p>
        </div>

        <div
          className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
          style={{ ...staggerStyle(4), ...darkWebCardAccentStyle }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="rounded-xl bg-accent/10 p-3 text-accent">
              <IconEye className="h-7 w-7" />
            </div>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${darkWebBadge.className}`}
            >
              {darkWebBadge.label}
            </span>
          </div>
          <p className="mt-4 text-sm font-medium text-muted">Dark Web Alerts</p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {!dataReady ? '—' : darkWeb}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted">{darkWebDescription}</p>
          {hasMonitoredDomains && monitoredDomainValues.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {monitoredDomainValues.slice(0, 3).map((domain) => (
                <span
                  key={domain}
                  className="rounded-full bg-foreground/8 px-2 py-0.5 font-mono text-xs text-muted"
                >
                  {domain}
                </span>
              ))}
              {monitoredDomainValues.length > 3 ? (
                <span className="text-xs text-muted">+{monitoredDomainValues.length - 3} more</span>
              ) : null}
            </div>
          ) : null}
          {!threatCheckLoading &&
          hasMonitoredDomains &&
          (otxThreatStatus === 'high' || otxThreatStatus === 'medium') ? (
            <Link
              to="/alerts"
              className="mt-3 inline-block text-xs font-semibold text-danger hover:underline"
            >
              View threat alerts →
            </Link>
          ) : null}
          {!hasMonitoredDomains ? (
            <Link to="/assets" className="mt-3 inline-block text-xs font-semibold text-accent hover:underline">
              Add a domain to monitor →
            </Link>
          ) : null}
        </div>

        <div
          className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6 sm:col-span-2 lg:col-span-1"
          style={staggerStyle(5)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="rounded-xl bg-accent/10 p-3 text-accent">
              <IconLaptop className="h-7 w-7" />
            </div>
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold"
              style={deviceRisk.badgeStyle}
            >
              {deviceRisk.badge}
            </span>
          </div>
          <p className="mt-4 text-sm font-medium text-muted">Device Risk Level</p>
          <p className="mt-1 text-3xl font-bold" style={deviceRisk.labelStyle}>
            {deviceRisk.label}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted">{deviceRisk.description}</p>
          {deviceRisk.label === 'None enrolled' ? (
            <Link
              to="/download"
              className="mt-2 inline-block text-xs font-semibold text-accent hover:underline"
            >
              Install agent — protect this device →
            </Link>
          ) : null}
        </div>
      </section>

      {monitoringStatus?.hasSoftStoppedAssets && lockedThreatCount > 0 ? (
        <section
          className="opacity-0 animate-fade-in-up rounded-2xl border border-danger/40 bg-danger/10 p-5 sm:p-6"
          style={staggerStyle(6)}
          aria-label="Locked threats"
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-foreground">
              🔒 {lockedThreatCount} threat{lockedThreatCount === 1 ? '' : 's'} detected — upgrade to view
            </p>
            <Link
              to="/marketplace"
              className="inline-flex shrink-0 text-sm font-semibold text-accent underline-offset-2 hover:underline"
            >
              View pricing →
            </Link>
          </div>
        </section>
      ) : null}

      <section
        className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
        style={staggerStyle(7)}
      >
        <h3 className="text-lg font-semibold text-foreground">What&apos;s Been Happening</h3>
        {activityItems.length === 0 ? (
          <p className="mt-5 text-sm text-muted">
            {dataReady ? 'No recent alerts yet.' : 'Loading activity…'}
          </p>
        ) : (
          <ul className="mt-5 space-y-4">
            {activityItems.map((item) => (
              <li key={item.key} className="flex gap-3 text-sm sm:text-base">
                <span
                  className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                    item.tone === 'red'
                      ? 'bg-danger'
                      : item.tone === 'amber'
                        ? 'bg-warning'
                        : 'bg-accent'
                  }`}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground">{item.text}</p>
                  {item.timeLabel ? (
                    <p className="mt-1 text-xs text-muted sm:text-sm">{item.timeLabel}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/alerts"
          className="mt-6 inline-flex items-center text-sm font-semibold text-accent hover:underline"
        >
          View all alerts →
        </Link>
      </section>

      {billingSummary && billingSummary.assetCount > 0 ? (
        <section
          className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
          style={staggerStyle(8)}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-foreground">Billing Summary</h3>
            <Link to="/settings" className="text-sm font-semibold text-accent hover:underline">
              Manage billing →
            </Link>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-surface/50 p-3">
              <p className="text-xs text-muted">Current assets monitored</p>
              <p className="mt-1 text-xl font-semibold text-foreground">{billingSummary.assetCount}</p>
            </div>
            <div className="rounded-lg border border-border bg-surface/50 p-3">
              <p className="text-xs text-muted">Trial assets</p>
              <p className="mt-1 text-xl font-semibold text-foreground">
                {billingSummary.trialAssetCount}
                {billingSummary.trialAssetCount > 0 && earliestTrialDaysLeft !== null
                  ? ` (${earliestTrialDaysLeft} day${earliestTrialDaysLeft === 1 ? '' : 's'} left)`
                  : ''}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-surface/50 p-3">
              <p className="text-xs text-muted">Estimated monthly cost after trials</p>
              <p className="mt-1 text-xl font-semibold text-foreground">
                {new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: billingSummary.currency || 'USD',
                  maximumFractionDigits: 0,
                }).format(billingSummary.estimatedMonthlyCost)}
                /mo
              </p>
            </div>
          </div>
        </section>
      ) : null}

      <section className="space-y-4 opacity-0 animate-fade-in-up" style={staggerStyle(9)}>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Your Weekly Security Brief</h3>
          <p className="mt-1 text-sm text-muted">
            {isPersonal
              ? 'Generated by AI based on your account activity this week'
              : 'Generated by AI for your business this week'}
          </p>
          <p className="mt-1 text-sm text-muted">
            {briefGeneratedAt
              ? (() => {
                  try {
                    return `Generated at ${briefGeneratedAt.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} today`;
                  } catch {
                    return 'Generated today';
                  }
                })()
              : briefLoading
                ? isPersonal
                  ? 'Generating your security brief...'
                  : 'Generating your business brief...'
                : ''}
          </p>
        </div>
        {briefLoading ? (
          <div
            className="rounded-2xl border border-border border-l-4 border-l-accent/30 bg-surface/60 p-6 sm:p-8"
            aria-busy="true"
            aria-label="Loading security brief"
          >
            <div className="space-y-4 animate-pulse">
              <div className="h-4 w-full max-w-2xl rounded-md bg-foreground/10" />
              <div className="h-4 w-full max-w-xl rounded-md bg-foreground/10" />
              <div className="h-4 w-full max-w-lg rounded-md bg-foreground/10" />
              <div className="h-4 w-full max-w-md rounded-md bg-foreground/10" />
            </div>
          </div>
        ) : briefError ? (
          <div className="rounded-2xl border border-border bg-surface/60 p-6 sm:p-8">
            <p className="text-sm text-muted">
              Unable to generate brief at this time. Please try again later.
            </p>
            <button
              type="button"
              disabled={briefLoading}
              onClick={() => void refreshBrief()}
              className="mt-4 inline-flex items-center justify-center rounded-lg border border-accent/40 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Refresh Brief
            </button>
          </div>
        ) : briefText ? (
          <div className="rounded-2xl border border-border border-l-4 border-l-accent bg-surface/60 p-6 sm:p-8">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground sm:text-base">
              {briefText}
            </p>
            <div className="mt-6 flex flex-col gap-4 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">
                Generated by ShieldSME AI
              </p>
              <button
                type="button"
                disabled={briefLoading}
                onClick={() => void refreshBrief()}
                className="inline-flex items-center justify-center rounded-lg border border-accent/40 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Refresh Brief
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="space-y-4 opacity-0 animate-fade-in-up" style={staggerStyle(10)}>
        <h3 className="text-lg font-semibold text-foreground">Recommended Next Steps</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: 'Connect Your Email',
              body: 'Monitor Gmail or Microsoft 365 for threats',
              btn: 'Connect Email',
              href: '/settings',
            },
            {
              title: 'Set Up WhatsApp Alerts',
              body: 'Get instant alerts on your phone',
              btn: 'Connect WhatsApp',
              href: '/settings',
            },
            {
              title: isPersonal ? 'Add Your Domain' : 'Add Your Business Domain',
              body: hasMonitoredDomains
                ? `${monitoredDomainValues.length} domain${monitoredDomainValues.length === 1 ? '' : 's'} on your watchlist — we check automatically for new threat signals.`
                : "We'll monitor it on the dark web for you",
              btn: 'Add Domain',
              href: '/assets',
            },
            ...(features.momoProtection
              ? [
                  {
                    title: 'Check for MoMo Fraud',
                    body: 'Paste suspicious messages to instantly detect scams',
                    btn: 'Open MoMo Protection',
                    href: '/momo',
                  },
                ]
              : []),
          ].map((card, i) => (
            <div
              key={card.title}
              className="opacity-0 animate-fade-in-up flex flex-col rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
              style={{ animationDelay: `${200 + i * 90}ms` }}
            >
              <h4 className="font-semibold text-foreground">{card.title}</h4>
              <p className="mt-2 flex-1 text-sm text-muted">{card.body}</p>
              <Link
                to={card.href}
                className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-accent/40 bg-accent/10 py-2.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/20"
              >
                {card.btn}
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
